import React from 'react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Lock, Volume2, VolumeX, UserPlus, Loader2, Clock, Check, Circle } from 'lucide-react';
import { isVideoUrl, resolveMediaUrl } from '@/lib/media';
import { useFriendship } from '@/hooks/useFriendship';

interface BlurredMediaProps {
  src: string;
  alt: string;
  shouldBlur: boolean;
  isFsk18: boolean;
  className?: string;
  containerClassName?: string;
  onClick?: () => void;
  loading?: 'lazy' | 'eager';
  decoding?: 'async' | 'sync' | 'auto';
  sizes?: string;
  autoPlayVideo?: boolean; // default true for autoplay muted previews
  showMuteToggle?: boolean; // default true
  poster?: string; // optional poster image for videos
  useViewportAutoplay?: boolean; // play/pause when in/out of viewport
  forceVideo?: boolean; // explicitly render as video
  loopVideo?: boolean; // loop control
  priority?: boolean; // high priority loading for above-the-fold images
  thumbnailUrl?: string; // low-res thumbnail for blur-up effect
  aspectRatio?: string; // CSS aspect-ratio to prevent layout shifts (e.g. "3/4")
  characterId?: number; // for friendship unlock option
}

export const BlurredMedia: React.FC<BlurredMediaProps> = ({
  src,
  alt,
  shouldBlur,
  isFsk18,
  className,
  containerClassName,
  onClick,
  loading = 'lazy',
  decoding = 'async',
  sizes,
  autoPlayVideo = true,
  showMuteToggle = true,
  poster,
  useViewportAutoplay = false,
  forceVideo,
  loopVideo = true,
  priority = false,
  thumbnailUrl,
  aspectRatio,
  characterId
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  // Friendship hook for unlock option
  const {
    isFriend,
    canRequest,
    isPendingFromUser,
    requestFriendship,
    isRequesting,
  } = useFriendship(characterId);

  const [mediaSrc, setMediaSrc] = React.useState(resolveMediaUrl(src));
  React.useEffect(() => setMediaSrc(resolveMediaUrl(src)), [src]);
  const [posterSrc, setPosterSrc] = React.useState(poster ? resolveMediaUrl(poster) : undefined);
  React.useEffect(() => setPosterSrc(poster ? resolveMediaUrl(poster) : undefined), [poster]);

  // Lazy loading with IntersectionObserver
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = React.useState(priority); // priority images load immediately
  const [isLoaded, setIsLoaded] = React.useState(false);

  // Resolve thumbnail URL for blur-up placeholder
  const resolvedThumbnailUrl = React.useMemo(
    () => thumbnailUrl ? resolveMediaUrl(thumbnailUrl) : undefined,
    [thumbnailUrl]
  );

  // IntersectionObserver for lazy loading (200px before viewport)
  React.useEffect(() => {
    if (priority || isInView) return; // Skip if priority or already in view

    const element = containerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [priority, isInView]);

  const [isMuted, setIsMuted] = React.useState(true);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  const isBlurred = shouldBlur;
  // Ableite MIME-Type für <source>, verbessert Browser-Kompatibilität bei Videos
  const lower = (mediaSrc || '').toLowerCase();
  const videoMime = lower.endsWith('.mp4')
    ? 'video/mp4'
    : lower.endsWith('.webm')
      ? 'video/webm'
      : lower.endsWith('.mov')
        ? 'video/quicktime'
        : lower.endsWith('.m4v')
          ? 'video/x-m4v'
          : lower.endsWith('.ogv') || lower.endsWith('.ogg')
            ? 'video/ogg'
            : undefined;
  // Nur als Video rendern, wenn die URL wie ein Video aussieht oder explizit forciert
  // oder eine bekannte/spielbare Extension erkennbar ist. Dadurch vermeiden wir z.B. .webp als Video.
  // Wichtig: Auch dann als Video rendern, wenn keine Extension erkennbar ist, die URL aber nach Video aussieht.
  const isVideo = forceVideo
    ? (isVideoUrl(mediaSrc) || Boolean(videoMime))
    : (isVideoUrl(mediaSrc) || Boolean(videoMime));

  const handlePrimaryClick = () => {
    if (!isBlurred && onClick) onClick();
  };

  const handleError = () => {
    // Bei Videos: src NICHT überschreiben, damit Debug/Netzwerk-Analyse möglich bleibt
    // Bei Bildern: sanfter Fallback auf transparentes Pixel
    if (!isVideo) {
      setMediaSrc('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8Xw8AAtMBgR4mL8wAAAAASUVORK5CYII=');
    }
    try { console.warn('[BlurredMedia] Media load error for src:', src); } catch {}
  };

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    try {
      v.muted = isMuted;
      if (autoPlayVideo && isVideo) {
        const playPromise = v.play();
        if (playPromise && typeof playPromise.then === 'function') {
          void playPromise.catch(() => {
            // Autoplay might be blocked; keep muted and ignore
          });
        }
      }
    } catch (_) {}
  }, [isMuted, mediaSrc, autoPlayVideo, isVideo]);

  // Viewport-based play/pause for performance
  React.useEffect(() => {
    if (!useViewportAutoplay || !isVideo) return;
    const v = videoRef.current;
    if (!v) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const el = entry.target as HTMLVideoElement;
        if (entry.isIntersecting) {
          try {
            const p = el.play();
            if (p && typeof (p as any).catch === 'function') {
              void (p).catch(() => {});
            }
          } catch {
            // ignore
          }
        } else {
          try {
            el.pause();
          } catch {
            // ignore
          }
        }
      });
    }, { threshold: 0.25 });
    observer.observe(v);
    return () => observer.disconnect();
  }, [useViewportAutoplay, mediaSrc, isVideo]);

  // Container style with aspect-ratio to prevent layout shifts
  const containerStyle: React.CSSProperties = aspectRatio
    ? { aspectRatio }
    : undefined as unknown as React.CSSProperties;

  return (
    <div
      ref={containerRef}
      className={cn('relative group h-full', containerClassName)}
      style={containerStyle}
    >
      {/* Blur-up placeholder (shown while loading or not in view) */}
      {resolvedThumbnailUrl && !isLoaded && (
        <img
          src={resolvedThumbnailUrl}
          alt=""
          aria-hidden="true"
          className={cn(
            'absolute inset-0 w-full h-full object-cover blur-lg scale-105',
            isLoaded && 'opacity-0 transition-opacity duration-300'
          )}
        />
      )}

      {/* Placeholder when not in view (no thumbnail available) */}
      {!isInView && !resolvedThumbnailUrl && (
        <div
          className={cn(
            'w-full h-full bg-muted animate-pulse',
            className
          )}
          aria-hidden="true"
        />
      )}

      {/* Actual media content - only rendered when in view */}
      {isInView && (
        <>
          {isVideo ? (
            <>
              <video
                ref={videoRef}
                src={videoMime ? undefined : mediaSrc}
                playsInline
                muted
                autoPlay={autoPlayVideo}
                loop={loopVideo}
                preload={autoPlayVideo ? 'metadata' : 'none'}
                poster={posterSrc}
                crossOrigin="anonymous"
                className={cn(
                  'w-full h-full object-cover transition-[opacity,transform] duration-300',
                  isBlurred && 'blur-3xl',
                  !isLoaded && resolvedThumbnailUrl && 'opacity-0',
                  className
                )}
                onClick={handlePrimaryClick}
                onError={handleError}
                onLoadedData={() => setIsLoaded(true)}
              >
                {videoMime && <source src={mediaSrc} type={videoMime} />}
              </video>
              {showMuteToggle && !isBlurred && (
                <button
                  type="button"
                  aria-label={isMuted ? t('video.unmute') : t('video.mute')}
                  className="absolute bottom-2 right-2 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMuted((m) => !m);
                    const v = videoRef.current;
                    if (v) v.muted = !isMuted;
                  }}
                >
                  {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
              )}
            </>
          ) : (
            <img
              src={mediaSrc}
              alt={alt}
              loading={priority ? 'eager' : loading}
              decoding={decoding}
              sizes={sizes || '(max-width: 768px) 100vw, 33vw'}
              {...(priority && { fetchpriority: 'high' as const })}
              className={cn(
                'w-full h-full object-cover transition-[opacity,transform] duration-300',
                isBlurred && 'blur-3xl',
                !isLoaded && resolvedThumbnailUrl && 'opacity-0',
                className
              )}
              onClick={handlePrimaryClick}
              onError={handleError}
              onLoad={() => setIsLoaded(true)}
            />
          )}
        </>
      )}

      {isBlurred && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <div className="text-center text-white p-token-md">
            <Lock className="w-12 h-12 mx-auto mb-token-sm" />
            <p className="font-medium mb-token-sm">{t('fsk18.content')}</p>
            {!user ? (
              <Button size="sm" variant="secondary" onClick={() => navigate('/login')}>
                {t('fsk18.loginToView')}
              </Button>
            ) : characterId ? (
              <div className="space-y-token-sm">
                <p className="text-xs text-white/70">{t('fsk18.unlockRequirements')}</p>

                {/* Requirement 1: Friendship */}
                <div className="flex items-center gap-token-sm">
                  {isFriend ? (
                    <div className="flex items-center gap-token-xs flex-1 min-w-0">
                      <Check className="w-4 h-4 text-success shrink-0" />
                      <span className="text-sm text-success">{t('fsk18.friendshipComplete')}</span>
                    </div>
                  ) : isPendingFromUser ? (
                    <div className="flex items-center gap-token-xs flex-1 min-w-0">
                      <Clock className="w-4 h-4 text-white/50 shrink-0" />
                      <span className="text-sm text-white/70">{t('friendship.status.pending')}</span>
                    </div>
                  ) : canRequest ? (
                    <div className="flex items-center gap-token-xs flex-1 min-w-0">
                      <Circle className="w-4 h-4 text-white/50 shrink-0" />
                      <Button
                        size="sm"
                        variant="gradient"
                        onClick={(e) => {
                          e.stopPropagation();
                          requestFriendship();
                        }}
                        disabled={isRequesting}
                        className="text-xs"
                      >
                        {isRequesting ? (
                          <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        ) : (
                          <UserPlus className="w-3 h-3 mr-1" />
                        )}
                        {t('friendship.actions.requestToUnlock')}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-token-xs flex-1 min-w-0">
                      <Circle className="w-4 h-4 text-white/50 shrink-0" />
                      <span className="text-sm text-white/50">{t('friendship.status.none')}</span>
                    </div>
                  )}
                </div>

                {/* Requirement 2: Premium */}
                <div className="flex items-center gap-token-sm">
                  {user.is_verified ? (
                    <div className="flex items-center gap-token-xs flex-1 min-w-0">
                      <Check className="w-4 h-4 text-success shrink-0" />
                      <span className="text-sm text-success">{t('fsk18.premiumComplete')}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-token-xs flex-1 min-w-0">
                      <Circle className="w-4 h-4 text-white/50 shrink-0" />
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/shop');
                        }}
                        className="text-xs"
                      >
                        {t('fsk18.upgradeToPremium')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Fallback: no characterId — only show premium upgrade */
              <div className="space-y-token-sm">
                <Button size="sm" variant="secondary" onClick={() => navigate('/shop')}>
                  {t('fsk18.upgradeToPremium')}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BlurredMedia;


